import { GridSystem } from '@gameplay/building/GridSystem';
import { RoomDefinition, RoomTile, RoomCategory, ServiceLocation } from './RoomTypes';

export interface ProximityConfig {
  categories: Array<{
    category: RoomCategory;
    weight: number;
  }>;
  maxDistance: number;
}

const DEFAULT_CONFIG: ProximityConfig = {
  categories: [
    { category: 'dining', weight: 0.25 },
    { category: 'medical', weight: 0.3 },
    { category: 'bathroom', weight: 0.2 },
    { category: 'recreation', weight: 0.15 },
    { category: 'utility', weight: 0.1 }
  ],
  maxDistance: 12
};

export class RoomQualityCalculator {
  private readonly gridSystem: GridSystem;
  private readonly config: ProximityConfig;

  constructor(gridSystem: GridSystem, config: ProximityConfig = DEFAULT_CONFIG) {
    this.gridSystem = gridSystem;
    this.config = config;
  }

  public calculateProximityScore(
    room: RoomDefinition,
    servicesByCategory: Map<RoomCategory, ServiceLocation[]>
  ): number {
    if (room.tiles.length === 0) {
      return 0;
    }

    const centroid = this.getCentroid(room.tiles);

    let score = 0;
    let weightTotal = 0;

    this.config.categories.forEach(({ category, weight }) => {
      const services = servicesByCategory.get(category) ?? [];
      if (services.length === 0) {
        return;
      }

      const distance = this.getDistanceToNearest(centroid, services);
      const normalized = this.normalizeDistance(distance, this.config.maxDistance);
      score += normalized * weight;
      weightTotal += weight;
    });

    if (weightTotal === 0) {
      return 0;
    }

    return score / weightTotal;
  }

  private getCentroid(tiles: RoomTile[]): { x: number; y: number; floor: number } {
    const sum = tiles.reduce(
      (acc, tile) => {
        acc.x += tile.gridX;
        acc.y += tile.gridY;
        acc.floor += tile.floor;
        return acc;
      },
      { x: 0, y: 0, floor: 0 }
    );

    const count = tiles.length;

    return {
      x: sum.x / count,
      y: sum.y / count,
      floor: Math.round(sum.floor / count)
    };
  }

  private getDistanceToNearest(
    centroid: { x: number; y: number; floor: number },
    services: ServiceLocation[]
  ): number {
    let shortest = Infinity;

    services.forEach((service) => {
      if (service.floor !== centroid.floor) {
        // Penalize floor differences heavily to simulate elevator travel.
        const floorPenalty = Math.abs(service.floor - centroid.floor) * 4;
        shortest = Math.min(shortest, floorPenalty + this.manhattanDistance(centroid, service));
      } else {
        shortest = Math.min(shortest, this.manhattanDistance(centroid, service));
      }
    });

    return shortest;
  }

  private manhattanDistance(
    a: { x: number; y: number },
    b: { gridX: number; gridY: number }
  ): number {
    return Math.abs(a.x - b.gridX) + Math.abs(a.y - b.gridY);
  }

  private normalizeDistance(distance: number, maxDistance: number): number {
    if (distance === Infinity) {
      return 0;
    }

    if (distance <= 0) {
      return 1;
    }

    return Math.max(0, 1 - distance / maxDistance);
  }
}
